import { useState } from "react";
import * as Sentry from "@sentry/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import OnboardingStep1 from "./OnboardingStep1";
import OnboardingStep2 from "./OnboardingStep2";
import OnboardingStep3 from "./OnboardingStep3";
import OnboardingStep4 from "./OnboardingStep4";
import OnboardingCompletion from "./OnboardingCompletion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { useRecomputeFitScores } from "@/hooks/useRecomputeFitScores";
import { sanitizeOwnProfileUpdate } from "@/lib/profiles/ownProfileUpdate";
import { cn } from "@/lib/utils";

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  /** Bright agent UI: the dialog is portaled outside .theme-bright, so it carries the scope itself. */
  bright?: boolean;
}

const defaultData = {
  // Step 1
  name: "",
  email: "",
  password: "",
  // Step 2
  location: "",
  licenseNumber: "",
  licenseState: "",
  yearsInBusiness: 3,
  mlsAffiliation: "",
  // Step 3
  priorities: [] as string[],
  // Step 4
  preferredTeamSize: "small",
  culturePrefs: {
    collaborative: 50,
    structured: 50,
    traditional: 50,
  },
};

const OnboardingModal = ({ isOpen, onClose, onComplete, bright = false }: OnboardingModalProps) => {
  const { user, isLoading: authLoading } = useAuth();
  const recomputeFitScores = useRecomputeFitScores();

  // Item 15 follow-up — the app-level mount only ever surfaces this wizard to
  // already-authenticated agents, for whom Step 1 (an account-creation form:
  // name/email/password/Google) does not apply. Skip it and start at Step 2.
  // The legacy unauthenticated path (the dead Index.tsx "Get Started" flow) is
  // preserved defensively: firstStep falls back to 1 with the full 4-step UI.
  const isAuthenticated = !authLoading && !!user;
  const authName = (user?.user_metadata?.full_name as string | undefined) ?? "";
  const firstStep = isAuthenticated ? 2 : 1;

  const [currentStep, setCurrentStep] = useState(firstStep);
  const [data, setData] = useState(defaultData);
  const [isSaving, setIsSaving] = useState(false);

  // Reset to the correct starting step (and fresh data) the moment the modal
  // opens — using the adjust-state-during-render-on-prop-change pattern so the
  // step is corrected before paint, never flashing Step 1. The gate only opens
  // the wizard once auth is settled, so firstStep/authName are accurate here.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setCurrentStep(firstStep);
      setData({ ...defaultData, name: authName });
    }
  }

  // Internal currentStep numbering stays 1–4 (+5 completion) so any
  // step-indexed logic is untouched; only the displayed counter collapses to
  // the effective step count for authenticated users ("of 3" vs "of 4").
  const totalSteps = 4;
  const displayTotalSteps = isAuthenticated ? 3 : 4;
  const stepSequence = isAuthenticated ? [2, 3, 4] : [1, 2, 3, 4];
  const displayStep = currentStep - (firstStep - 1);
  const progress = currentStep <= totalSteps ? (displayStep / displayTotalSteps) * 100 : 100;

  // Resolves true when the answers were saved (or there was no signed-in user
  // to save for — the legacy unauthenticated path), false when the save failed
  // so the caller can keep the agent on the last step to retry.
  const persistOnboardingData = async (): Promise<boolean> => {
    if (!user) return true;
    setIsSaving(true);
    try {
      // Item 15 follow-up — harmonize the wizard's write with its downstream
      // readers. Previously this wrote a flat profile_data blob that nothing
      // read: compute_fit_score reads the 12 dedicated pref_* columns, and the
      // AgentProfilePage reads profile_data.basic.* + the license_number column.
      // We now write all three, merging (not overwriting) profile_data.

      // Read-modify-write merge into profile_data, mirroring AgentProfilePage's
      // saveToDatabase: pull the existing JSONB and merge sub-objects so we
      // never clobber basic/production/goals/opportunity/about/links/wishlist
      // written by the profile editor.
      // A failed read is not an empty document: merging into {} would replace
      // every key the profile editor wrote, so stop before the update.
      const { data: current, error: readError } = await supabase
        .from("profiles")
        .select("profile_data")
        .eq("user_id", user.id)
        .maybeSingle();

      if (readError) throw readError;

      const existingPd = (current?.profile_data as any) || {};

      // basic.* — the keys BasicInfoSection reads. Location is rendered from
      // basic.city (the component keeps city/location in sync), so set both.
      const mergedBasic = {
        ...(existingPd.basic || {}),
        location: data.location,
        city: data.location,
        licenseNumber: data.licenseNumber,
        licenseState: data.licenseState,
        yearsExperience: data.yearsInBusiness,
        mls: data.mlsAffiliation,
      };

      // opportunity.* — only the keys IdealOpportunitySection actually reads:
      // teamSize (ids align) and the three culture sliders the wizard collects
      // (0–100 scale aligns). desiredSplit is intentionally dropped — no reader.
      const mergedOpportunity = {
        ...(existingPd.opportunity || {}),
        teamSize: data.preferredTeamSize,
        culture_collaborative: data.culturePrefs.collaborative,
        culture_structured: data.culturePrefs.structured,
        culture_traditional: data.culturePrefs.traditional,
      };

      // goals.priorities — CareerGoalsSection renders these ids. The wizard's
      // "tech" id is "technology" there, so alias it; "splits" has no goals
      // chip but survives as the pref_importance_commission signal below.
      const mergedGoals = {
        ...(existingPd.goals || {}),
        priorities: (data.priorities || []).map((p) => (p === "tech" ? "technology" : p)),
      };

      const mergedProfileData = {
        ...existingPd,
        basic: mergedBasic,
        opportunity: mergedOpportunity,
        goals: mergedGoals,
        onboardingCompleted: true,
      };

      // pref_* dedicated columns — the source compute_fit_score reads. Mirror
      // BrokerageWishlistSection's scale (importance 1–5, 5 = top; pill value
      // "required" for wants_leads). Only write columns the agent expressed a
      // preference for — a partial update with the same semantics as
      // saveWishlistPatch — so unselected dimensions stay NULL and
      // compute_fit_score skips them rather than counting a zero weight.
      const hasPriority = (id: string) => (data.priorities || []).includes(id);
      const prefUpdates: Record<string, string | number> = {};
      if (hasPriority("leads")) prefUpdates.pref_wants_leads = "required";
      if (hasPriority("splits")) prefUpdates.pref_importance_commission = 5;
      if (hasPriority("mentorship")) prefUpdates.pref_importance_training = 5;
      if (hasPriority("tech")) prefUpdates.pref_importance_technology = 5;
      if (hasPriority("balance")) prefUpdates.pref_importance_work_life = 5;

      const { error } = await supabase
        .from("profiles")
        .update(sanitizeOwnProfileUpdate({
          full_name: data.name || undefined,
          // Dedicated license_number column — AgentProfilePage reads it as the
          // source of truth (falling back to basic.licenseNumber). Omit when
          // empty so we never clobber an existing value.
          license_number: data.licenseNumber || undefined,
          profile_data: mergedProfileData,
          ...prefUpdates,
        }))
        .eq("user_id", user.id);

      if (error) throw error;

      // Item 14 — preferences are now persisted, so fan-call compute_fit_score
      // across every published org to populate agent_org_fit_scores for this
      // agent (compute_fit_score reads the agent's prefs server-side, so the
      // save above must land first). Fire-and-forget: the hook invalidates the
      // ["agent-fit-scores"] query when it finishes, so /explore refreshes its
      // badges reactively — no need to await before navigating. A catch is
      // attached so a rejection is never an unhandled promise — per-org
      // failures are already isolated and logged inside the hook.
      recomputeFitScores.mutateAsync().catch((err) => {
        console.error("[onboarding] fit-score recompute failed:", err);
        Sentry.captureException(err, {
          tags: { component: "onboarding-modal", stage: "recompute-fit-scores" },
        });
      });

      toast({ title: "Profile saved", description: "Your preferences have been saved successfully." });
      return true;
    } catch (err: any) {
      console.error("Failed to save onboarding data:", err);
      toast({ title: "Save failed", description: err.message || "Could not save your profile. Please try again.", variant: "destructive" });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // Only a saved profile reaches the completion screen; on failure the
      // wizard stays on this step with its answers so the agent can retry.
      const saved = await persistOnboardingData();
      if (saved) setCurrentStep(5); // Completion
    }
  };

  const handleBack = () => {
    // Floor at firstStep so authenticated users never land on the skipped Step 1.
    if (currentStep > firstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // The fit-score fan-call invalidates the ["agent-fit-scores"] query when it
    // finishes, so /explore refreshes its match badges reactively — no need to
    // await it here. Close immediately.
    onComplete();
    onClose();
    // Reset for next time (the open-handler above re-applies firstStep/name).
    setCurrentStep(firstStep);
    setData(defaultData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          "max-w-lg p-0 overflow-hidden max-h-[90vh] overflow-y-auto",
          // bg-background is required here, not decorative: .theme-bright
          // redefines the --background token, so the dialog must actually
          // paint that variable for the new scope to be visible at all.
          bright && "theme-bright bg-background",
        )}
      >
        <DialogTitle className="sr-only">Set up your agent profile</DialogTitle>
        <DialogDescription className="sr-only">
          A few quick steps to personalize your team matches.
        </DialogDescription>
        {/* Header */}
        {currentStep <= totalSteps && (
          <div className="sticky top-0 bg-background z-10 px-6 pt-6 pb-4 border-b border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {stepSequence.map((step) => (
                  <div
                    key={step}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      step === currentStep
                        ? "bg-primary"
                        : step < currentStep
                        ? "bg-primary/50"
                        : "bg-muted"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                Step {displayStep} of {displayTotalSteps}
              </span>
            </div>
            <Progress value={progress} className="h-1" />
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {currentStep === 1 && (
            <OnboardingStep1
              data={{ name: data.name, email: data.email, password: data.password }}
              onUpdate={(stepData) => setData({ ...data, ...stepData })}
              onNext={handleNext}
            />
          )}

          {currentStep === 2 && (
            <OnboardingStep2
              data={{
                location: data.location,
                licenseNumber: data.licenseNumber,
                licenseState: data.licenseState,
                yearsInBusiness: data.yearsInBusiness,
                mlsAffiliation: data.mlsAffiliation,
              }}
              onUpdate={(stepData) => setData({ ...data, ...stepData })}
              onNext={handleNext}
              // When Step 2 is the first step (authenticated), there is nowhere
              // to go back to — omit onBack so Step 2 hides its Back button.
              onBack={currentStep > firstStep ? handleBack : undefined}
            />
          )}

          {currentStep === 3 && (
            <OnboardingStep3
              data={{
                priorities: data.priorities,
              }}
              onUpdate={(stepData) => setData({ ...data, ...stepData })}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 4 && (
            <OnboardingStep4
              data={{
                preferredTeamSize: data.preferredTeamSize,
                culturePrefs: data.culturePrefs,
              }}
              onUpdate={(stepData) => setData({ ...data, ...stepData })}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 5 && (
            <OnboardingCompletion
              userName={data.name}
              onViewMatches={handleComplete}
            />
          )}

          {/* Low-weight skip — closes without setting onboardingCompleted, so
              the AgentOnboardingBanner keeps nudging and the wizard stays
              re-openable. Hidden on the completion step. */}
          {currentStep <= totalSteps && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
              >
                Skip for now
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
